/**
 * @file Foot Facts calculator — client-side port of the backend transformation layer.
 *
 * Ported from wiivv/ms-measurement → src/lib/computeFootFacts.js (the module that
 * generates the `footFacts` object driving the Foot Facts email via Klaviyo).
 * This is the COMPLETE module — classification (arch/width/length), shoe sizes,
 * percentiles (the 0–10 arrow indices), left/right ratios, and uniqueness grids —
 * so the web report classifies with exactly the email's logic (decision D10).
 *
 * The source follows the ISO/TS 19407 specification 1st edition (2015-02-01).
 *
 * Deliberate divergences from the backend source (decision D11 — each documented for
 * the backend team in docs/projects/foot-facts-web-port/HANDOFF-foot-facts-backend.md):
 *   1. sameWidthClassesRatio bug fixed — the backend passes raw width NUMBERS to
 *      populationWithSimilarFootWidths(), which compares width CLASS STRINGS, so it
 *      always returns 9.5. This port passes the computed width classes.
 *   2. console.log noise removed (browser asset).
 *
 * Body indentation and naming (including the `footWidthUniqunessCutoff` typo) are kept
 * identical to the backend source so side-by-side diffing stays trivial.
 *
 * Input: measurements in millimetres —
 *   { leftFootArchHeight, leftFootLength, leftFootWidth,
 *     rightFootArchHeight, rightFootLength, rightFootWidth }
 *
 * Exposed as window.FootFactsCalculator (same pattern as size-calculator.js and
 * arch-height-calculator.js).
 */
/* eslint-disable indent, no-unused-vars, semi */
(function () {
'use strict';

// Guard against multiple loads — this asset is included by more than one block.
if (window.FootFactsCalculator) return;

const footFactsConfig = {
  archHeight: {
    lowFactor: 0.0325,
    highFactor: 0.0506,
    low: 'low',
    high: 'high',
    average: 'average',
  },
  width: {
    lowFactor: 0.3562,
    highFactor: 0.4025,
    low: 'narrow',
    high: 'wide',
    average: 'average'
  },
  length: {
    lowFactor: 236.44,
    highFactor: 273.60,
    low: 'below_average',
    high: 'above_average',
    average: 'average',
  },
  // Based on the https://github.com/wiivv/flemish-fire/blob/master/footFacts_simple.py #unique_per_db
  footLengthUniquenessCutoff: [0, 175, 185, 195, 205, 215, 225, 235, 245, 255, 265, 275, 285, 295, 305, 315, 325],
  footWidthUniqunessCutoff: [0, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125, 130],
  lowArchHeightUniquenessGrid: {
    0: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    175: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    185: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    195: [0.00, 0.00, 0.00, 0.01, 0.02, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    205: [0.00, 0.00, 0.00, 0.02, 0.06, 0.05, 0.04, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    215: [0.00, 0.00, 0.00, 0.07, 0.20, 0.24, 0.19, 0.05, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    225: [0.00, 0.00, 0.00, 0.05, 0.33, 0.81, 0.72, 0.25, 0.06, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00],
    235: [0.00, 0.00, 0.00, 0.07, 0.25, 0.79, 1.17, 0.77, 0.18, 0.05, 0.01, 0.00, 0.00, 0.00, 0.00],
    245: [0.00, 0.00, 0.00, 0.01, 0.09, 0.39, 1.08, 0.95, 0.46, 0.13, 0.02, 0.00, 0.00, 0.00, 0.00],
    255: [0.00, 0.00, 0.00, 0.01, 0.01, 0.10, 0.55, 0.89, 0.83, 0.30, 0.08, 0.01, 0.00, 0.00, 0.00],
    265: [0.00, 0.00, 0.00, 0.00, 0.00, 0.02, 0.24, 0.69, 0.79, 0.50, 0.12, 0.02, 0.00, 0.00, 0.00],
    275: [0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.05, 0.24, 0.39, 0.27, 0.11, 0.03, 0.01, 0.00, 0.00],
    285: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.03, 0.12, 0.13, 0.05, 0.04, 0.01, 0.00, 0.00],
    295: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.01, 0.03, 0.02, 0.01, 0.01, 0.00, 0.00],
    305: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.01, 0.01, 0.01, 0.00, 0.00, 0.00],
    315: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.00, 0.00, 0.00, 0.00],
    325: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
  },
  averageArchHeightUniquenessGrid: {
    0: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    175: [0.00, 0.01, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    185: [0.00, 0.01, 0.01, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    195: [0.00, 0.01, 0.01, 0.00, 0.02, 0.01, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    205: [0.00, 0.00, 0.03, 0.09, 0.15, 0.13, 0.07, 0.01, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    215: [0.00, 0.00, 0.03, 0.15, 0.65, 0.85, 0.56, 0.14, 0.02, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00],
    225: [0.00, 0.00, 0.00, 0.26, 1.50, 2.60, 1.99, 0.82, 0.20, 0.02, 0.01, 0.00, 0.00, 0.00, 0.00],
    235: [0.00, 0.00, 0.02, 0.15, 1.21, 3.70, 4.39, 2.35, 0.73, 0.12, 0.01, 0.01, 0.00, 0.00, 0.00],
    245: [0.00, 0.00, 0.00, 0.04, 0.45, 2.23, 4.48, 3.78, 1.65, 0.46, 0.08, 0.02, 0.00, 0.00, 0.00],
    255: [0.00, 0.00, 0.00, 0.01, 0.13, 0.80, 2.82, 4.08, 2.97, 1.16, 0.21, 0.04, 0.01, 0.00, 0.00],
    265: [0.00, 0.00, 0.00, 0.00, 0.03, 0.16, 1.29, 3.34, 3.51, 1.89, 0.42, 0.10, 0.01, 0.00, 0.00],
    275: [0.00, 0.00, 0.00, 0.00, 0.01, 0.05, 0.34, 1.29, 2.23, 1.89, 0.64, 0.12, 0.02, 0.00, 0.00],
    285: [0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.04, 0.35, 0.71, 0.93, 0.57, 0.16, 0.01, 0.00, 0.00],
    295: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.07, 0.17, 0.20, 0.11, 0.07, 0.01, 0.01, 0.00],
    305: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.02, 0.04, 0.04, 0.02, 0.02, 0.01, 0.00],
    315: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.02, 0.01, 0.02, 0.01, 0.00, 0.00],
    325: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
  },
  highArchHeightUniquenessGrid: {
    0: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    175: [0.00, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    185: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    195: [0.00, 0.01, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    205: [0.00, 0.01, 0.00, 0.01, 0.01, 0.02, 0.02, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    215: [0.00, 0.00, 0.00, 0.03, 0.10, 0.10, 0.05, 0.01, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
    225: [0.00, 0.00, 0.00, 0.05, 0.25, 0.43, 0.37, 0.11, 0.03, 0.01, 0.00, 0.00, 0.00, 0.00, 0.00],
    235: [0.00, 0.00, 0.01, 0.05, 0.19, 0.58, 0.82, 0.41, 0.14, 0.03, 0.01, 0.00, 0.00, 0.00, 0.00],
    245: [0.00, 0.00, 0.00, 0.01, 0.08, 0.48, 0.86, 0.67, 0.32, 0.05, 0.02, 0.01, 0.00, 0.00, 0.00],
    255: [0.00, 0.00, 0.00, 0.01, 0.04, 0.29, 0.61, 0.83, 0.57, 0.24, 0.04, 0.01, 0.00, 0.00, 0.00],
    265: [0.00, 0.00, 0.00, 0.00, 0.01, 0.11, 0.42, 0.73, 0.74, 0.37, 0.14, 0.02, 0.01, 0.00, 0.00],
    275: [0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.15, 0.40, 0.40, 0.33, 0.12, 0.05, 0.02, 0.00, 0.00],
    285: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.04, 0.13, 0.25, 0.20, 0.13, 0.05, 0.01, 0.01, 0.00],
    295: [0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.01, 0.03, 0.06, 0.09, 0.09, 0.01, 0.01, 0.01, 0.00],
    305: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.02, 0.01, 0.02, 0.01, 0.00, 0.01, 0.00],
    315: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.01, 0.00, 0.01, 0.01, 0.00, 0.00, 0.00],
    325: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
  },
  // Based on the https://github.com/wiivv/flemish-fire/blob/master/flemishfirefiles/percentiles.json
  // The arrays depict the 0 to 100 percentile by 10 percentile increments. 0 percentile being at array index 0. In other words:
  // [0 percentile, 10 percentile, 20 percentile, 30 percentile, 40 percentile, 50 percentile, 60 percentile, 70 percentile, 80 percentile, 90 percentile, 100 percentile]
  percentilesArchHeight: [0.0128, 0.0306, 0.034, 0.0365, 0.0388, 0.041, 0.0433, 0.0459, 0.049, 0.0533, 0.1203],
  percentilesLength: [175.874, 231.742, 238.559, 243.979, 249.218, 254.588, 260.192, 265.84, 271.935, 279.678, 318.532],
  percentilesWidth: [0.294, 0.351, 0.36, 0.367, 0.373, 0.378, 0.384, 0.39, 0.398, 0.409, 0.491],
};

function classifyArchHeight({ archHeight, length }) {
  let archHeightClass;
  archHeight = Math.round(archHeight);
  length = Math.round(length);

  const nah = archHeight / length;
  const { lowFactor, highFactor, low, average, high } = footFactsConfig.archHeight;

  if (nah <= lowFactor) {
    archHeightClass = low;
  } else if (nah > lowFactor && nah <= highFactor) {
    archHeightClass = average;
  } else if (nah > highFactor) {
    archHeightClass = high;
  }

  return archHeightClass;
}

function classifyWidth({ length, width }) {
  let widthClass;
  length = Math.round(length);
  width = Math.round(width);

  const nfw = width / length;
  const { lowFactor, highFactor, low, average, high } = footFactsConfig.width;

  if (nfw <= lowFactor) {
    widthClass = low;
  } else if (nfw > lowFactor && nfw <= highFactor) {
    widthClass = average;
  } else if (nfw > highFactor) {
    widthClass = high;
  }

  return widthClass;
}

function classifyLength({ length }) {
  let lengthClass;
  const { lowFactor, highFactor, low, average, high } = footFactsConfig.length;

  if (length <= lowFactor) {
    lengthClass = low;
  } else if (length > lowFactor && length <= highFactor) {
    lengthClass = average;
  } else if (length > highFactor) {
    lengthClass = high;
  }

  return lengthClass;
}

function classifyFoot({ archHeight, length, width }) {
  return {
    lengthClass: classifyLength({ length }),
    widthClass: classifyWidth({ length, width }),
    archHeightClass: classifyArchHeight({ archHeight, length }),
  };
}

function round1Digit(x) {
  return Math.round(x * 10) / 10;
}

function round1DigitObj(obj) {
  const rounded1DigitObj = {};
  for (let key of Object.keys(obj)) {
    rounded1DigitObj[key] = round1Digit(obj[key]);
  }
  return rounded1DigitObj;
}

function roundObj(obj) {
  const roundedObj = {};
  for (let key of Object.keys(obj)) {
    roundedObj[key] = Math.round(obj[key]);
  }
  return roundedObj;
}

function round4Digits(x) {
  return Math.round(x * 10000) / 10000;
}

function round5(x) {
  return Math.round(x / 5) * 5;
}

function roundFirstDecimal5(x) {
  x = x * 10;
  return round5(x) / 10 ;
}

function eurSize(length) {
  return roundFirstDecimal5((length + (2 * 6.67)) / 6.67);
}

function ukSize(length) {
  return roundFirstDecimal5(((length + (2 * 8.47)) / 8.47) - 25);
}

function usMenSize(uk) {
  return uk + 1;
}

function usWomenSize(uk) {
  return uk + 2;
}

function getShoeSize(length) {
  const uk = ukSize(length);
  const eur = eurSize(length);
  const usMen = usMenSize(uk);
  const usWomen = usWomenSize(uk);
  const mondopoint = round5(length);

  return {
    uk,
    eur,
    mondopoint,
    usMen,
    usWomen,
  };
}

function getFootPercentiles({ archHeight, length, width }) {
  const normalizedArchHeight = round4Digits(archHeight / length);
  const normalizedWidth = round4Digits(width / length);
  const { percentilesArchHeight, percentilesLength, percentilesWidth } = footFactsConfig;

  return {
    archHeightPercentile: getPercentile(normalizedArchHeight, percentilesArchHeight),
    lengthPercentile: getPercentile(length, percentilesLength),
    widthPercentile: getPercentile(normalizedWidth, percentilesWidth),
  };
}

function getPercentile(value, percentiles) {
  if (value <= percentiles[0]) {
    return 0;
  } else if (value > percentiles[percentiles.length - 1]) {
    return 10;
  } else {
    return percentiles.findIndex(percentile => value <= percentile);
  }
}

function percentUniqueness(length, width, archHeightClass) {
  let uniquenessPercentGrid;
  if (archHeightClass === footFactsConfig.archHeight.low) {
    uniquenessPercentGrid = footFactsConfig.lowArchHeightUniquenessGrid;
  } else if (archHeightClass === footFactsConfig.archHeight.average) {
    uniquenessPercentGrid = footFactsConfig.averageArchHeightUniquenessGrid;
  } else {
    uniquenessPercentGrid = footFactsConfig.highArchHeightUniquenessGrid;
  }

  length = Math.round(length);
  width = Math.round(width);

  let lengthCutoffIndex, lengthCutoff, widthCutoffIndex;

  if (length <= 0) {
    lengthCutoffIndex = 0;
  } else if (length > footFactsConfig.footLengthUniquenessCutoff[footFactsConfig.footLengthUniquenessCutoff.length - 1]) {
    lengthCutoffIndex = footFactsConfig.footLengthUniquenessCutoff.length - 1;
  } else {
    lengthCutoffIndex = footFactsConfig.footLengthUniquenessCutoff.findIndex(cutoffValue => length <= cutoffValue) - 1;
  }

  lengthCutoff = footFactsConfig.footLengthUniquenessCutoff[lengthCutoffIndex];

  if (width <= 0) {
    widthCutoffIndex = 0;
  } else if (width > footFactsConfig.footWidthUniqunessCutoff[footFactsConfig.footWidthUniqunessCutoff.length - 1]) {
    widthCutoffIndex = footFactsConfig.footWidthUniqunessCutoff.length - 1;
  } else {
    widthCutoffIndex = footFactsConfig.footWidthUniqunessCutoff.findIndex(cutoffValue => width <= cutoffValue) - 1;
  }

  return uniquenessPercentGrid[lengthCutoff][widthCutoffIndex];
}

// it always compare the left foot relative to the right foot
function compareFootFeatures(leftFoot, rightFoot) {
  const lengthRatio = (leftFoot.length - rightFoot.length) * 100 / rightFoot.length;
  const widthRatio = (leftFoot.width - rightFoot.width) * 100 / rightFoot.width;
  const archHeightRatio = (leftFoot.archHeight - rightFoot.archHeight) * 100 / rightFoot.archHeight;

  return {
    lengthRatio,
    widthRatio,
    archHeightRatio,
  };
}

function populationWithSimilarFootWidths(leftFootWidthClass, rightFootWidthClass) {
  const { low, average } = footFactsConfig.width;
  let populationPercentage;

  if (leftFootWidthClass === low) {
    if (rightFootWidthClass === low) {
      populationPercentage = 8.9;
    } else if (rightFootWidthClass === average) {
      populationPercentage = 6.2;
    } else {
      populationPercentage = 0.02;
    }
  } else if (leftFootWidthClass === average) {
    if (rightFootWidthClass === low) {
      populationPercentage = 5.8;
    } else if (rightFootWidthClass === average) {
      populationPercentage = 57.6;
    } else {
      populationPercentage = 6;
    }
  } else {
    if (rightFootWidthClass === low) {
      populationPercentage = 0.07;
    } else if (rightFootWidthClass === average) {
      populationPercentage = 6;
    } else {
      populationPercentage = 9.5;
    }
  }

  return populationPercentage;
}

function computeFootFacts({ measurements } = {}) {
  if (!measurements) return {};

  let {
    leftFootArchHeight,
    leftFootLength,
    leftFootWidth,
    rightFootArchHeight,
    rightFootLength,
    rightFootWidth } = measurements;

  leftFootArchHeight = parseFloat(leftFootArchHeight);
  leftFootLength = parseFloat(leftFootLength);
  leftFootWidth = parseFloat(leftFootWidth);
  rightFootArchHeight = parseFloat(rightFootArchHeight);
  rightFootLength = parseFloat(rightFootLength);
  rightFootWidth = parseFloat(rightFootWidth);

  let leftFootDimensions = { length: leftFootLength, width: leftFootWidth, archHeight: leftFootArchHeight };
  leftFootDimensions = roundObj(leftFootDimensions);

  const leftFootClasses = classifyFoot(leftFootDimensions);
  const leftFootPercentiles = getFootPercentiles(leftFootDimensions);
  const leftShoeSizes = getShoeSize(leftFootLength);
  const leftPercentUniqueness = percentUniqueness(leftFootLength, leftFootWidth, leftFootClasses.archHeightClass);

  let rightFootDimensions = { length: rightFootLength, width: rightFootWidth, archHeight: rightFootArchHeight };
  rightFootDimensions = roundObj(rightFootDimensions);

  const rightFootClasses = classifyFoot(rightFootDimensions);
  const rightFootPercentiles = getFootPercentiles(rightFootDimensions);
  const rightShoeSizes = getShoeSize(rightFootLength);
  const rightPercentUniqueness = percentUniqueness(rightFootLength, rightFootWidth, rightFootClasses.archHeightClass);

  const feetRatios = compareFootFeatures(leftFootDimensions, rightFootDimensions);
  // D11 fix — the backend passes raw widths (numbers) here, but the function compares
  // width class strings ('narrow'/'average'/'wide'), so it always falls through to 9.5.
  // See docs/projects/foot-facts-web-port/HANDOFF-foot-facts-backend.md (bug 1).
  const sameWidthClassesRatio = populationWithSimilarFootWidths(leftFootClasses.widthClass, rightFootClasses.widthClass);

  const footFactsResult = {
    leftFoot: {
      ...leftFootDimensions,
      ...leftFootClasses,
      ...leftFootPercentiles,
      shoeSizes: leftShoeSizes,
      percentUnique: leftPercentUniqueness
    },
    rightFoot: {
      ...rightFootDimensions,
      ...rightFootClasses,
      ...rightFootPercentiles,
      shoeSizes: rightShoeSizes,
      percentUnique: rightPercentUniqueness
    },
    ...round1DigitObj(feetRatios),
    sameWidthClassesRatio,
  };
  return footFactsResult;
}

// Expose on window (same pattern as size-calculator.js / arch-height-calculator.js).
// computeFootFacts is the main entry point; the helpers are exposed for reuse — e.g.
// arch classification from arch-height-calculator.js per D10 — and for testing.
window.FootFactsCalculator = {
  computeFootFacts,
  classifyFoot,
  classifyArchHeight,
  classifyWidth,
  classifyLength,
  getShoeSize,
  getFootPercentiles,
  getPercentile,
  percentUniqueness,
  compareFootFeatures,
  populationWithSimilarFootWidths,
  footFactsConfig,
};
})();
